import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createRefetchContainer, graphql } from 'react-relay';
import serverVars from 'server-vars';
import { CUSTOMER_TYPE_TRADE } from '../../constants/userConstants';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers';
import {
    getUserZipCode,
    getUserSessionCountryCode,
} from 'dibs-buyer-layout/exports/regionalInfoHelpers';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants';

/* Components */
import { SearchCorrectionsProvider } from '../useSearchCorrections';
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespCollectionsModules } from '../SbRespCollectionsModules/SbRespCollectionsModules';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';

/* Styles */
import '../SbRespLayout/SbRespLayout.scss'; // temp fix for FINDING-13444, will eventually change base style in DBL

class SbRespCollectionsLayoutComponent extends React.Component {
    constructor() {
        super();

        this.handleUserRefetch = this.handleUserRefetch.bind(this);
        this.handleSbRefetch = this.handleSbRefetch.bind(this);

        this.relayVars = {};
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const { isClient } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();
        const userZipCode = getUserZipCode();
        const userCountryCode = getUserSessionCountryCode();
        const fetchSb = variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE;
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient });
        this.doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
                userZipCode,
                userCountryCode,
                fetchRegionalInfo: true,
                localRecentHistoryItemIds,
                fetchUserRecentHistoryItem:
                    localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            },
            ...args
        );
    }

    handleSbRefetch(variables, renderVariables, callback) {
        this.doRefetch({ ...variables, fetchSb: true }, renderVariables, () => {
            callback();

            this.props.runNotificationQueue();
        });
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = { ...this.relayVars, ...variables };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchSb: true },
            callback
        );
    }

    render() {
        const { viewer, localeDomain, reinitTracking } = this.props;
        const user = viewer.user || null;
        const { itemSearch, regionalInfo = null, shippingFilters = null } = viewer;

        return (
            <React.Fragment>
                <SharedUserAndRelayVarLoader onUserRefetch={this.handleUserRefetch} />
                <SearchCorrectionsProvider itemSearch={itemSearch}>
                    {({ searchCorrectionTerm }) => (
                        <CurrencyProvider
                            render={({ currency }) => (
                                <React.Fragment>
                                    <SbSharedTrackingContainer
                                        itemSearch={itemSearch}
                                        user={user}
                                        currency={currency}
                                        refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                                        reinitTracking={reinitTracking}
                                        searchCorrectionTerm={searchCorrectionTerm}
                                    />
                                    <SbSharedHead
                                        itemSearch={itemSearch}
                                        localeDomain={localeDomain}
                                    />
                                    <SbSharedRefetchContainer
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        onSbRefetch={this.handleSbRefetch}
                                        regionalInfo={regionalInfo}
                                        shippingFilters={shippingFilters}
                                    />
                                    <SbRespCollectionsModules
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        user={user}
                                        currency={currency}
                                        isClient={!!this.relayVars.isClient}
                                        shippingFilters={shippingFilters}
                                    />
                                </React.Fragment>
                            )}
                        />
                    )}
                </SearchCorrectionsProvider>
            </React.Fragment>
        );
    }
}

SbRespCollectionsLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
};

SbRespCollectionsLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

const mapStateToProps = state => {
    const { intlVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
    };
};

export const SbRespCollectionsLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespCollectionsLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespCollectionsLayout_viewer on Viewer
                @argumentDefinitions(
                    hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                    userZipCode: { type: "String", defaultValue: "0" }
                    userCountryCode: { type: "String", defaultValue: "" }
                ) {
                ...SbRespCollectionsModules_viewer
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                ) @include(if: $fetchSb) {
                    # Used by SearchCorrectionsProvider
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespCollectionsModules_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespCollectionsModules_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespCollectionsModules_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespCollectionsLayoutRefetchQuery(
            $userId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $uriRef: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH]
            $contentModuleId: String = ""
            $eligibleAbTest: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = "0"
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
        ) {
            viewer {
                ...SbRespCollectionsModules_viewer
                itemSearch(
                    uriRef: $uriRef
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    eligibleAbTest: $eligibleAbTest
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                ) @include(if: $fetchSb) {
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespCollectionsModules_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespCollectionsModules_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespCollectionsModules_shippingFilters
                }
            }
        }
    `
);
